import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { History, ChevronLeft, ChevronRight, RotateCw, CheckCheck, Search, RotateCcw, FileText, AlertCircle } from 'lucide-react';
import { useToast } from '../../../context/ToastContext';
import { useAppSelector } from '../../../store/hooks';
import { selectUser } from '../../../store/selectors';
import {
  fetchDispatches,
  fetchDispatchOptions,
  retryDispatches,
  resolveDispatch,
  errorMessage,
} from '../../../api/document/documentCentreApi';
import DocumentHistoryDrawer from '../DocumentCentre/components/DocumentHistoryDrawer';
import {
  CHANNEL_LABEL,
  RESULT,
  canSendDocuments,
  formatStamp,
  plural,
} from '../DocumentCentre/documentCentre.constants';
import styles from '../DocumentCentre/DocumentCentre.module.css';

/**
 * Dispatch history (SUNSKY Documents spec v1.2, §17): every attempt with its
 * exact document version, recipient, channel, staff member, message, result,
 * error and the financial-release reason. Resolved errors leave the
 * unresolved overview; retries only ever re-send failed items.
 */

const EMPTY = {
  dateFrom: '',
  dateTo: '',
  q: '',
  types: '',
  channel: '',
  staff: '',
  result: '',
  unresolvedOnly: false,
  includePrints: false,
};

const RESULT_OPTIONS = [
  { value: '', label: 'Any result' },
  { value: 'sent', label: 'Sent' },
  { value: 'share_started', label: 'WhatsApp share started' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'processing', label: 'Still processing' },
  { value: 'completed', label: 'Completed (print or download)' },
];

const DispatchHistory = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const user = useAppSelector(selectUser);
  const canSend = canSendDocuments(user);
  const [draft, setDraft] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [options, setOptions] = useState({ staff: [], types: [] });
  const [refreshKey, setRefreshKey] = useState(0);
  const [busy, setBusy] = useState(null);
  const [resolving, setResolving] = useState(null);
  const [note, setNote] = useState('');
  const [historyId, setHistoryId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const requestId = useRef(0);

  useEffect(() => {
    fetchDispatchOptions()
      .then(setOptions)
      .catch(async (err) => showToast(await errorMessage(err, 'Could not load the filter options'), 'error'));
  }, [showToast]);

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    setError('');
    fetchDispatches({
      ...applied,
      unresolvedOnly: applied.unresolvedOnly ? 'true' : '',
      includePrints: applied.includePrints ? 'true' : '',
      page,
      pageSize: 25,
    })
      .then((res) => {
        if (id === requestId.current) setResult(res);
      })
      .catch(async (err) => {
        if (id !== requestId.current) return;
        const message = await errorMessage(err, 'Could not load the dispatch history');
        setError(message);
        showToast(message, 'error');
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, [applied, page, refreshKey, showToast]);

  const apply = (next) => {
    setApplied(next);
    setPage(1);
  };
  const set = (key) => (e) =>
    setDraft((prev) => ({ ...prev, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const rows = result?.data?.rows || [];
  const summary = result?.data?.summary || {};
  const pagination = result?.meta || { page: 1, totalPages: 1, total: 0 };

  const retry = async (row) => {
    setBusy(row.id);
    try {
      const res = await retryDispatches([row.id]);
      const outcome = res.results?.[0];
      if (outcome?.status === 'sent') showToast(`${row.document_number || 'Document'} sent again`, 'success');
      else showToast(outcome?.error || 'The retry did not succeed', 'error');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      showToast(await errorMessage(err, 'Retry failed'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const resolve = async (row) => {
    setBusy(row.id);
    try {
      await resolveDispatch(row.id, note.trim());
      showToast('Marked as resolved', 'success');
      setResolving(null);
      setNote('');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      showToast(await errorMessage(err, 'Could not mark it resolved'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const cards = [
    { key: 'total', label: 'Records', value: summary.total ?? 0, sub: applied.includePrints ? 'including prints and downloads' : 'dispatches only' },
    { key: 'sent', label: 'Sent', value: summary.sent ?? 0, sub: 'accepted by the mail provider' },
    { key: 'share', label: 'WhatsApp shares started', value: summary.shareStarted ?? 0, sub: 'opened manually' },
    {
      key: 'unresolved',
      label: 'Unresolved errors',
      value: summary.unresolved ?? 0,
      sub: `${summary.failed ?? 0} failed in total`,
      onClick: () => {
        const next = { ...applied, unresolvedOnly: true };
        setDraft(next);
        apply(next);
      },
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.titleBlock}>
          <div className={styles.manager}>
            <History size={20} />
            Dispatch History
            <span className={styles.managerCount}>{pagination.total}</span>
          </div>
          <span className={styles.subtitle}>
            Every email and WhatsApp dispatch with its exact document version and result. Prints and downloads are kept
            in the history but never count as a dispatch.
          </span>
        </div>
        <div className={styles.toolbarLinks}>
          <button type="button" className={styles.linkBtn} onClick={() => navigate('/documents')}>
            <FileText size={14} /> All documents
          </button>
        </div>
      </div>

      <div className={styles.summaryBar}>
        {cards.map((card) =>
          card.onClick ? (
            <button
              type="button"
              key={card.key}
              className={`${styles.summaryCard} ${styles.summaryCardButton}`}
              onClick={card.onClick}
            >
              <span className={styles.summaryLabel}>{card.label}</span>
              <span className={styles.summaryValue}>{card.value}</span>
              <span className={styles.summarySub}>{card.sub}</span>
            </button>
          ) : (
            <div key={card.key} className={styles.summaryCard}>
              <span className={styles.summaryLabel}>{card.label}</span>
              <span className={styles.summaryValue}>{card.value}</span>
              <span className={styles.summarySub}>{card.sub}</span>
            </div>
          )
        )}
      </div>

      <form
        className={styles.filterPanel}
        onSubmit={(e) => {
          e.preventDefault();
          apply({ ...draft });
        }}
      >
        <div className={styles.filterRow}>
          <div className={`${styles.field} ${styles.fieldWide}`}>
            <span className={styles.fieldLabel}>Booking, customer, document or recipient</span>
            <input className={styles.input} type="search" value={draft.q} onChange={set('q')} placeholder="Search" />
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Date</span>
            <div className={styles.dateRange}>
              <input className={styles.input} type="date" value={draft.dateFrom} onChange={set('dateFrom')} aria-label="From" />
              <input className={styles.input} type="date" value={draft.dateTo} onChange={set('dateTo')} aria-label="To" />
            </div>
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Document type</span>
            <select className={styles.select} value={draft.types} onChange={set('types')}>
              <option value="">Any type</option>
              {options.types.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Channel</span>
            <select className={styles.select} value={draft.channel} onChange={set('channel')}>
              <option value="">Any channel</option>
              {Object.entries(CHANNEL_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Staff member</span>
            <select className={styles.select} value={draft.staff} onChange={set('staff')}>
              <option value="">Anyone</option>
              {options.staff.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Result</span>
            <select className={styles.select} value={draft.result} onChange={set('result')}>
              {RESULT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className={styles.filterRow} style={{ alignItems: 'center' }}>
          <label className={styles.checkboxLabel}>
            <input type="checkbox" className={styles.checkbox} checked={draft.unresolvedOnly} onChange={set('unresolvedOnly')} />
            Unresolved errors only
          </label>
          <label className={styles.checkboxLabel}>
            <input type="checkbox" className={styles.checkbox} checked={draft.includePrints} onChange={set('includePrints')} />
            Include prints and downloads
          </label>
          <div className={styles.filterActions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => {
                setDraft(EMPTY);
                apply(EMPTY);
              }}
            >
              <RotateCcw size={14} /> Clear filters
            </button>
            <button type="submit" className={styles.primaryBtn} disabled={loading}>
              <Search size={14} /> Apply filters
            </button>
          </div>
        </div>
      </form>

      <div className={styles.tableSection}>
        <div className={styles.tableCard}>
          {error && !rows.length ? (
            <div className={styles.errorBox}>
              <AlertCircle size={16} /> {error}
            </div>
          ) : loading && !rows.length ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner} />
              <span className={styles.loadingText}>Loading the dispatch history...</span>
            </div>
          ) : !rows.length ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <History size={26} />
              </div>
              <div className={styles.emptyTitle}>Nothing recorded yet</div>
              <div className={styles.emptyHint}>Dispatches appear here once documents are sent from the Documents centre or a booking.</div>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Document</th>
                  <th>Booking</th>
                  <th>Channel</th>
                  <th>Recipient</th>
                  <th>Staff</th>
                  <th>Result</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const meta = RESULT[row.status] || { label: row.status, color: '#475569' };
                  const open = (row.status === 'failed' || row.status === 'processing') && !row.resolved_at;
                  return (
                    <tr key={row.id}>
                      <td>
                        <span>{formatStamp(row.completed_at || row.created_at)}</span>
                        {row.retry_of && <span className={styles.mutedBlock}>Retry of #{row.retry_of}</span>}
                      </td>
                      <td>
                        <span className={styles.docType}>{row.typeLabel}</span>
                        <span className={styles.mutedBlock}>
                          <span className={styles.docNumber}>{row.document_number || '-'}</span>
                          <span className={styles.versionTag}>v{row.document_version}</span>
                        </span>
                      </td>
                      <td>
                        {row.bookingId ? (
                          <button type="button" className={styles.bookingLink} onClick={() => navigate(`/bookings/${row.bookingId}`)}>
                            {row.booking_reference}
                          </button>
                        ) : (
                          <span>{row.booking_reference || '-'}</span>
                        )}
                        <span className={styles.mutedBlock}>{row.customerName || '-'}</span>
                      </td>
                      <td>
                        {CHANNEL_LABEL[row.channel] || row.channel}
                        {row.purpose === 'internal' && <span className={styles.mutedBlock}>Internal copy</span>}
                        {!row.isDispatch && <span className={styles.mutedBlock}>Not a dispatch</span>}
                      </td>
                      <td>
                        {row.recipient_address ? (
                          <>
                            <span>{row.recipient_address}</span>
                            {row.recipient_name && <span className={styles.mutedBlock}>{row.recipient_name}</span>}
                            {!row.is_intended && <span className={styles.warnText}>Not the booking contact</span>}
                          </>
                        ) : (
                          <span className={styles.muted}>-</span>
                        )}
                      </td>
                      <td>{row.staffName || <span className={styles.muted}>System</span>}</td>
                      <td>
                        <span className={styles.statusText} style={{ color: meta.color }}>
                          {meta.label}
                        </span>
                        {row.error_message && <span className={styles.reasonText}>{row.error_message}</span>}
                        {row.resolved_at && (
                          <span className={styles.mutedBlock}>
                            Resolved {formatStamp(row.resolved_at)}: {row.resolution_note}
                          </span>
                        )}
                        {row.release_note && <span className={styles.mutedBlock}>Released: {row.release_note}</span>}
                        {row.subject && (
                          <button
                            type="button"
                            className={styles.moreToggle}
                            onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                          >
                            {expanded === row.id ? 'Hide message' : 'Show message'}
                          </button>
                        )}
                        {expanded === row.id && (
                          <span className={styles.mutedBlock} style={{ whiteSpace: 'pre-wrap', maxWidth: 360 }}>
                            {row.subject}
                            {'\n\n'}
                            {row.message_text}
                          </span>
                        )}
                        {resolving === row.id && (
                          <div className={styles.filterRow} style={{ marginTop: 6 }}>
                            <input
                              className={styles.input}
                              autoFocus
                              value={note}
                              placeholder="How was it resolved?"
                              onChange={(e) => setNote(e.target.value)}
                            />
                            <button
                              type="button"
                              className={styles.secondaryBtn}
                              disabled={note.trim().length < 3 || busy === row.id}
                              onClick={() => resolve(row)}
                            >
                              Save
                            </button>
                          </div>
                        )}
                      </td>
                      <td>
                        <div className={styles.rowActions}>
                          {open && row.status === 'failed' && row.channel === 'email' && canSend && (
                            <button type="button" onClick={() => retry(row)} disabled={busy === row.id} title="Send this item again">
                              <RotateCw size={13} /> Retry
                            </button>
                          )}
                          {open && canSend && (
                            <button
                              type="button"
                              onClick={() => {
                                setResolving(resolving === row.id ? null : row.id);
                                setNote('');
                              }}
                              title="Mark as resolved"
                            >
                              <CheckCheck size={13} />
                            </button>
                          )}
                          <button type="button" onClick={() => setHistoryId(row.document_id)} title="Document history">
                            <History size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className={styles.footerBar}>
          <span className={styles.paginationInfo}>
            Page {pagination.page} of {pagination.totalPages} ({plural(pagination.total, 'record')})
          </span>
          <div className={styles.paginationControls}>
            <button
              type="button"
              className={styles.pageBtn}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
            <span className={styles.paginationInfo}>{pagination.page}</span>
            <button
              type="button"
              className={styles.pageBtn}
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={pagination.page >= pagination.totalPages}
              aria-label="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {historyId && (
        <DocumentHistoryDrawer
          documentId={historyId}
          onClose={() => setHistoryId(null)}
          onChanged={() => setRefreshKey((k) => k + 1)}
          onOpenBooking={(doc) => doc.bookingId && navigate(`/bookings/${doc.bookingId}`)}
        />
      )}
    </div>
  );
};

export default DispatchHistory;
